<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import WebUploader from 'webuploader'
import 'webuploader/dist/webuploader.css'

const uploadedImageList = ref([])
let uploaderInstance = null

// 初始化WebUploader
const initUploader = () => {
  uploaderInstance = WebUploader.create({
    swf: '/Uploader.swf', // 从WebUploader官方包中取出该文件放到public目录下
    server: '/weixin/upfile', // 替换为你的后端图片上传接口
    pick: '#image-picker',
    auto: true,
    // 仅允许上传图片类型
    accept: {
      title: '图片文件',
      extensions: 'jpg,jpeg,png,gif,webp',
      mimeTypes: 'image/*'
    },
    fileSingleSizeLimit: 10 * 1024 * 1024, // 单张图片最大5MB
    fileNumLimit: 9 // 最多同时上传9张图片
  })

  // 核心：实时更新上传进度
  uploaderInstance.on('uploadProgress', function (file, percentage) {
    const progressVal = Math.round(percentage * 100)
    // 找到对应图片项，更新进度值，Vue会自动驱动视图更新
    const targetItem = uploadedImageList.value.find(item => item.id === file.id)
    if (targetItem) {
      targetItem.progress = progressVal
    }
  })

  // 1. 图片加入队列后，生成上传前本地预览
  uploaderInstance.on('fileQueued', (file) => {
    uploaderInstance.makeThumb(file, (error, previewSrc) => {
      if (error) return
      // 先把本地预览临时加入列表，提升用户体验
      uploadedImageList.value.push({
        id: file.id,
        url: previewSrc,
        isTemp: true, // 标记为本地临时预览图
        progress: 0, // 初始化进度为0%
        statusText: '等待上传'
      })
    }, 200, 200) // 预览图尺寸200*200
  })

  // 2. 上传成功后，替换临时预览图为后端返回的正式图片地址
  uploaderInstance.on('uploadSuccess', (file, response) => {
    // 过滤掉之前的临时预览项，替换为后端返回的真实图片地址
    const targetIndex = uploadedImageList.value.findIndex(item => item.id === file.id)
    console.log(response, uploadedImageList)
    if (targetIndex !== -1) {
      uploadedImageList.value[targetIndex] = {
        id: file.id,
        // 这里对应后端返回的图片地址字段，根据你的接口返回结构调整
        url: response.filename,
        isTemp: false,
        progress: 100,
        statusText: '上传成功'
      }
    }
  })
  // 上传失败状态更新
  uploaderInstance.on('uploadError', (file) => {
    const targetItem = uploadedImageList.value.find(item => item.id === file.id)
    if (targetItem) {
      targetItem.statusText = '上传失败'
      targetItem.progress = '0'
    }
  })

  // 错误统一捕获
  uploaderInstance.on('error', (type) => {
    const errorMap = {
      'F_EXCEED_SIZE': '单张图片不能超过10MB',
      'Q_EXCEED_NUM_LIMIT': '最多只能上传9张图片',
      'F_DUPLICATE': '该图片已在上传队列中'
    }
    alert(errorMap[type] || '上传出错')
  })
}

// 手动触发上传
// const startUpload = () => {
//   uploaderInstance.upload()
// }

// 删除已上传图片
const deleteImage = (fileId) => {
  uploadedImageList.value = uploadedImageList.value.filter(item => item.id !== fileId)
  uploaderInstance.removeFile(fileId, true)
}

// 组件挂载后初始化上传实例
onMounted(() => {
  nextTick(() => initUploader())
})

// 组件销毁时释放实例，避免内存泄漏
onUnmounted(() => {
  uploaderInstance && uploaderInstance.destroy()
})
</script>

<template>
  <div class="image-upload-wrap">
    <!-- 图片上传列表区域 -->
    <div class="uploaded-image-list">
      <div v-for="item in uploadedImageList" :key="item.id" class="image-item">
        <img :src="item.url" alt="上传图片" class="upload-img">
        <span class="delete-btn" @click="deleteImage(item.id)">×</span>
        <!-- 上传中显示进度条，上传完成后自动隐藏 -->
        <div v-if="item.isTemp" class="progress-mask">
          <div class="progress-bar" :style="{ width: `${item.progress}%` }"></div>
          <span class="progress-text">{{ item.progress }}%</span>
        </div>
        <p class="status-text">{{ item.statusText }}</p>
      </div>
      <div class="upload-actions">
        <div id="image-picker" class="picker-btn">
          <i class="plus-icon">+</i>
          <!-- 隐藏原生文件输入框，WebUploader会自动接管点击事件 -->
          <input type="file" accept="image/*" hidden>
        </div>
        <!-- <button type="primary" @click="startUpload">开始上传</button> -->
      </div>
    </div>
  </div>
</template>

<style scoped>
.image-upload-wrap {
  max-width: 700px;
  margin: 30px auto;
}

.uploaded-image-list {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.image-item {
  position: relative;
  width: 120px;
  height: 150px;
  /* 增加高度容纳底部状态区域 */
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #eee;
}

.upload-img {
  width: 100%;
  height: 120px;
  object-fit: cover;
}

.delete-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  border-radius: 50%;
  text-align: center;
  line-height: 24px;
  cursor: pointer;
}

.picker-btn {
  width: 120px;
  height: 120px;
  background: #f5f5f5;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 30px;
  border: 1px solid #000;
}

.picker-btn:hover {
  background: #eeeeee;
}

.plus-icon {
  font-size: 28px;
  color: #c0c4cc;
  font-weight: 300;

}

.upload-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

/* 新增进度条遮罩样式 */
.progress-mask {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 120px;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.progress-bar {
  width: 0%;
  height: 6px;
  background: #409eff;
  border-radius: 3px;
  transition: width 0.15s;
  opacity: 0;
}

.progress-text {
  margin-top: 6px;
  color: #fff;
  font-size: 14px;
}

.status-text {
  margin: 4px 0 0 0;
  font-size: 11px;
  text-align: center;
  color: #606266;
}
</style>
<style>
.webuploader-pick,
.webuploader-pick-hover {
  background: #f5f5f5 !important;
}
</style>